import { useCallback, useEffect, useRef } from 'react';

/**
 * Guards work that answers late. Call the returned function when the work starts:
 * it snapshots what is selected now and hands back a check that is true only while
 * the same thing is still selected and the page is still mounted.
 */
const useSelectionGuard = (selectionKey: string) => {
  const selectionRef = useRef(selectionKey);
  selectionRef.current = selectionKey;
  const mountedRef = useRef(true);

  // Set on every setup, not just the first: an effect replay must not leave the guard dead.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => {
    const startedOn = selectionRef.current;
    return () => mountedRef.current && selectionRef.current === startedOn;
  }, []);
};

export default useSelectionGuard;
